import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Clock, MapPin, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ZaloIcon } from './zalo-icon';
import './Footer.scss';

const Footer: React.FC = () => {
  const { t } = useTranslation();

  return (
    <footer className="ftr" aria-label="Footer" data-reveal data-anim="up">

      {/* ── CTA Strip */}
      <div className="ftr__cta-strip">
        <div className="ftr__container">
          <div className="ftr__cta-strip__inner">
            <div>
              <p className="ftr__cta-strip__label">{t('footer.cta.label')}</p>
              <p className="ftr__cta-strip__title">{t('footer.cta.title')}</p>
            </div>
            <div className="ftr__cta-strip__actions">
              <a href="tel:0374888419" className="ftr__cta-btn ftr__cta-btn--primary">
                <Phone size={16} /> {t('footer.cta.callBtn')}
              </a>
              <Link to="/lien-he" className="ftr__cta-btn ftr__cta-btn--outline">
                {t('footer.cta.contactBtn')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main body */}
      <div className="ftr__body">
        <div className="ftr__container">
          <div className="ftr__grid">

            {/* Col 1 — Brand */}
            <div className="ftr__brand-col">
              <img
                src="/assets/img/LOGO GHTM NEW 23.3 DONE-01.png"
                alt="GHTM Logo"
                className="ftr__logo-img"
              />
              <p className="ftr__tagline">{t('footer.brand.tagline')}</p>
              <div className="ftr__contact-list">
                <a href="tel:0374888419" className="ftr__contact-item">
                  <span className="ftr__contact-icon-wrap"><Phone size={14} /></span>
                  0374 888 419
                </a>
                <a href="mailto:contact@mklexpress.com" className="ftr__contact-item">
                  <span className="ftr__contact-icon-wrap"><Mail size={14} /></span>
                  contact@mklexpress.com
                </a>
                <span className="ftr__contact-item">
                  <span className="ftr__contact-icon-wrap"><MapPin size={14} /></span>
                  60 Trương Công Giai, Cầu Giấy, HN
                </span>
              </div>
            </div>

            {/* Col 2 — Quick links */}
            <div className="ftr__col">
              <h3 className="ftr__col-title">{t('footer.nav.title')}</h3>
              <ul className="ftr__link-list">
                {([
                  { to: '/trang-chu',  key: 'footer.nav.home' },
                  { to: '/gioi-thieu', key: 'footer.nav.about' },
                  { to: '/tra-cuu',    key: 'footer.nav.track' },
                  { to: '/tin-tuc',    key: 'footer.nav.news' },
                  { to: '/lien-he',    key: 'footer.nav.contact' },
                ] as const).map(link => (
                  <li key={link.to}>
                    <Link to={link.to} className="ftr__link">
                      <ChevronRight size={13} />
                      {t(link.key)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Col 3 — Hours */}
            <div className="ftr__col">
              <h3 className="ftr__col-title">{t('footer.hours.title')}</h3>
              <div className="ftr__hours">
                <div className="ftr__hours-row">
                  <Clock size={14} className="ftr__hours-icon" />
                  <div>
                    <p className="ftr__hours-line"><strong>{t('footer.hours.weekdays')}</strong>&nbsp;8:30 – 17:30</p>
                    <p className="ftr__hours-line"><strong>{t('footer.hours.saturday')}</strong>&nbsp;8:30 – 15:00</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Col 4 — Connect */}
            <div className="ftr__col">
              <h3 className="ftr__col-title">{t('footer.social.title')}</h3>
              <div className="ftr__socials">
                <a
                  href="https://zalo.me/3257775966701342779"
                  target="_blank" rel="noopener noreferrer"
                  className="ftr__social-btn"
                  aria-label="Zalo"
                >
                  <ZaloIcon className="ftr__social-zalo-ico" />
                  <span>Zalo</span>
                </a>
                <a
                  href="https://www.facebook.com/giaohangthongminh"
                  target="_blank" rel="noopener noreferrer"
                  className="ftr__social-btn ftr__social-btn--fb"
                  aria-label="Facebook"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                  </svg>
                  <span>Facebook</span>
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── Bottom bar */}
      <div className="ftr__bottom">
        <div className="ftr__container">
          <div className="ftr__bottom__inner">
            <p className="ftr__copy">{t('footer.copy.main')}</p>
          </div>
        </div>
      </div>

    </footer>
  );
};

export default Footer;
